import { useQuery, keepPreviousData } from "@tanstack/react-query";
import {
  SearchFilters as ApiSearchFilters,
  SearchResponse,
} from "../../services/searchApi";
import {
  EntityTypePlural,
  getEntityTypeByPlural,
} from "../../types/entityRegistry";
import { queryKeys } from "./keys";

type EntityType = EntityTypePlural;

interface UseSearchResultsParams {
  /** Fully-built API filter object (entity-type-specific fields already resolved). */
  filters: ApiSearchFilters;
  /** Which entity type endpoint to call. */
  entityType: EntityType;
  /** Whether the query should execute. */
  enabled?: boolean;
}

async function executeSearch(
  entityType: EntityType,
  filters: ApiSearchFilters,
): Promise<SearchResponse> {
  const search = getEntityTypeByPlural(entityType)?.search;
  if (!search) {
    throw new Error(`Entity type '${entityType}' is not yet implemented`);
  }
  return search(filters);
}

/**
 * TanStack Query hook for the search list view.
 *
 * The caller is responsible for building the full `ApiSearchFilters` object
 * (including pagination offset, sort, and entity-type-specific fields).
 * This hook simply executes the search and caches the result.
 *
 * Uses `keepPreviousData` so the table stays populated while new results load.
 */
export function useSearchResults({
  filters,
  entityType,
  enabled = true,
}: UseSearchResultsParams) {
  return useQuery({
    queryKey: queryKeys.search.results(entityType, filters),
    queryFn: () => executeSearch(entityType, filters),
    enabled,
    staleTime: 60 * 1000,
    gcTime: 5 * 60 * 1000,
    placeholderData: keepPreviousData,
  });
}
